import {
    ArrowLeftOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    FileTextOutlined,
    PrinterOutlined,
    SafetyCertificateOutlined,
    BankOutlined,
    BookOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Col,
    ConfigProvider,
    DatePicker,
    Descriptions,
    Divider,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Row,
    Spin,
    Statistic,
    Table,
    Tag,
    Tabs,
    Typography,
} from 'antd';
import frFR from 'antd/locale/fr_FR';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import axiosInstance from '../../../variables/api/axiosInstance';
import { displayNumbers } from '../../../variables/numbersFunctions';

dayjs.locale('fr');

const { Text, Title } = Typography;

const STATUT_CONFIG = {
    BROUILLON: { color: 'orange', label: 'Brouillon',  icon: <ClockCircleOutlined /> },
    VALIDE:    { color: 'blue',   label: 'Validé',     icon: <CheckCircleOutlined /> },
    PAYE:      { color: 'green',  label: 'Payé',       icon: <DollarOutlined /> },
};

// ─── Onglet 1 : Récapitulatif ─────────────────────────────────────────────────

const TabRecap = ({ arrete }) => {
    const detail = (() => {
        try { return JSON.parse(arrete.detailJournalierData || '[]'); }
        catch { return []; }
    })();

    // Collecter tous les types d'avenant présents
    const typesAvenant = [...new Set(
        detail.flatMap(j => (j.details || []).map(d => d.typeAvenant))
    )].sort();

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            fixed: 'left',
            width: 110,
            render: (d) => dayjs(d).format('DD/MM/YYYY'),
        },
        {
            title: 'Total véhicules',
            dataIndex: 'totalVehicules',
            align: 'right',
            width: 120,
            render: (v) => <strong>{(v || 0).toLocaleString('fr-FR')}</strong>,
        },
        ...typesAvenant.map(type => ({
            title: type,
            key: type,
            align: 'right',
            width: 110,
            render: (_, row) => {
                const found = (row.details || []).filter(d => d.typeAvenant === type);
                if (!found.length) return '—';
                const nb      = found.reduce((s, d) => s + d.nb, 0);
                const montant = found.reduce((s, d) => s + d.montantTTC, 0);
                return (
                    <span>
                        <Text type="secondary" style={{ fontSize: 11 }}>{nb}v </Text>
                        {displayNumbers(montant)}
                    </span>
                );
            },
        })),
        {
            title: 'Total TTC',
            dataIndex: 'totalTTC',
            align: 'right',
            fixed: 'right',
            width: 140,
            render: (v) => <strong>{displayNumbers(v)}</strong>,
        },
    ];

    const totaux = {
        totalVehicules: detail.reduce((s, j) => s + (j.totalVehicules || 0), 0),
        totalTTC:       detail.reduce((s, j) => s + (j.totalTTC || 0), 0),
    };

    return (
        <>
            {/* Agrégats */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                {[
                    { title: 'VL', value: arrete.nbVL || 0 },
                    { title: 'Camions', value: arrete.nbCamions || 0 },
                    { title: 'Primes TTC', value: displayNumbers(arrete.primesTTCTotal), suffix: 'F' },
                    { title: 'Primes nettes HT', value: displayNumbers(arrete.primesNettesTotal), suffix: 'F' },
                    { title: 'Taxes (6%)', value: displayNumbers(arrete.taxesTotal), suffix: 'F' },
                    { title: 'Commission STAC', value: displayNumbers(arrete.commissionSTAC), suffix: 'F' },
                    { title: 'Transitaires', value: displayNumbers(arrete.commissionTransitaires), suffix: 'F' },
                    { title: 'Prévention routière', value: displayNumbers(arrete.preventionRoutiere), suffix: 'F' },
                ].map((item, i) => (
                    <Col key={i} xs={12} md={6} style={{ marginBottom: 8 }}>
                        <Card size="small">
                            <Statistic title={item.title} value={item.value} suffix={item.suffix} />
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Charges saisies */}
            <Card size="small" title="Charges manuelles" style={{ marginBottom: 16 }}>
                <Descriptions size="small" column={3} bordered>
                    <Descriptions.Item label="Sinistres après oct. 2012">
                        {displayNumbers(arrete.sinistresApresOct2012)} F
                    </Descriptions.Item>
                    <Descriptions.Item label="Sinistres avant oct. 2012">
                        {displayNumbers(arrete.sinistresAvantOct2012)} F
                    </Descriptions.Item>
                    <Descriptions.Item label="Charges exceptionnelles">
                        {displayNumbers(arrete.chargesExceptionnelles)} F
                    </Descriptions.Item>
                    <Descriptions.Item label="Report mois précédent">
                        {displayNumbers(arrete.reportMoisPrecedent)} F
                    </Descriptions.Item>
                    <Descriptions.Item label="Versements bancaires">
                        {displayNumbers(arrete.totalVersementsBancaires)} F
                    </Descriptions.Item>
                    <Descriptions.Item label="Droit timbre">
                        {displayNumbers(arrete.droitTimbreTotal)} F
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {/* Tableau journalier */}
            <Card title="Détail journalier" size="small">
                {detail.length === 0 ? (
                    <Alert type="info" message="Aucun détail journalier disponible pour cet arrêté." />
                ) : (
                    <Table
                        dataSource={detail}
                        columns={columns}
                        rowKey="date"
                        size="small"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        summary={() => (
                            <Table.Summary.Row style={{ background: '#fafafa' }}>
                                <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    <strong>{totaux.totalVehicules.toLocaleString('fr-FR')}</strong>
                                </Table.Summary.Cell>
                                {typesAvenant.map((_, i) => (
                                    <Table.Summary.Cell key={i} index={i + 2} />
                                ))}
                                <Table.Summary.Cell index={typesAvenant.length + 2} align="right">
                                    <strong>{displayNumbers(totaux.totalTTC)}</strong>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        )}
                    />
                )}
            </Card>
        </>
    );
};

// ─── Onglet 2 : RETRO ─────────────────────────────────────────────────────────

const TabRetro = ({ arrete, onValider, validating }) => {
    const retrocessions = arrete.retrocessions || [];

    const columns = [
        { title: 'Compagnie', dataIndex: 'compagnieNom', key: 'nom', fixed: 'left', width: 160 },
        { title: 'Clé (%)', dataIndex: 'cleRepartition', align: 'right', width: 80,
          render: (v) => `${((v || 0) * 100).toFixed(0)} %` },
        { title: 'Primes rétrocédées', dataIndex: 'primesRetrocedees', align: 'right',
          render: (v) => displayNumbers(v) },
        { title: 'Dotation SAP (5%)', dataIndex: 'dotationSAP', align: 'right',
          render: (v) => displayNumbers(v) },
        { title: 'Frais admin (6%)', dataIndex: 'fraisAdministratifsVT', align: 'right',
          render: (v) => displayNumbers(v) },
        { title: 'Part STAC', dataIndex: 'partCommissionSTAC', align: 'right',
          render: (v) => displayNumbers(v) },
        { title: 'Part transitaires', dataIndex: 'partTransitaires', align: 'right',
          render: (v) => displayNumbers(v) },
        { title: 'Part prévention', dataIndex: 'partPrevention', align: 'right',
          render: (v) => displayNumbers(v) },
        { title: 'Part sin. ≥ 2012', dataIndex: 'partSinistresApres2012', align: 'right',
          render: (v) => displayNumbers(v) },
        { title: 'Part sin. < 2012', dataIndex: 'partSinistresAvant2012', align: 'right',
          render: (v) => displayNumbers(v) },
        { title: 'Part charges', dataIndex: 'partCharges', align: 'right',
          render: (v) => displayNumbers(v) },
        {
            title: 'Solde technique net',
            dataIndex: 'soldeTechniqueNet',
            align: 'right',
            fixed: 'right',
            width: 160,
            render: (v) => (
                <strong style={{ color: v >= 0 ? '#389e0d' : '#cf1322' }}>
                    {displayNumbers(v)} F
                </strong>
            ),
        },
        {
            title: 'Statut',
            key: 'paid',
            fixed: 'right',
            width: 100,
            render: (_, r) => r.paid
                ? <Tag color="green" icon={<CheckCircleOutlined />}>Payé</Tag>
                : <Tag color="orange" icon={<ClockCircleOutlined />}>En attente</Tag>,
        },
    ];

    const totalSolde = retrocessions.reduce((s, r) => s + (r.soldeTechniqueNet || 0), 0);

    return (
        <>
            {arrete.statut === 'BROUILLON' && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="L'arrêté est en brouillon — validez-le pour créer les rétrocessions par compagnie et verrouiller les règlements."
                    action={
                        <Popconfirm
                            title="Valider cet arrêté ?"
                            description="Les règlements du mois seront verrouillés et les rétrocessions calculées."
                            onConfirm={onValider}
                            okText="Valider"
                            cancelText="Annuler"
                        >
                            <Button type="primary" icon={<SafetyCertificateOutlined />} loading={validating}>
                                Valider l'arrêté
                            </Button>
                        </Popconfirm>
                    }
                />
            )}

            {retrocessions.length === 0 ? (
                <Alert type="info" message="Les rétrocessions seront disponibles après validation de l'arrêté." />
            ) : (
                <Table
                    dataSource={retrocessions}
                    columns={columns}
                    rowKey="retrocessionId"
                    size="small"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    summary={() => (
                        <Table.Summary.Row style={{ background: '#f0f5ff' }}>
                            <Table.Summary.Cell index={0} colSpan={11}><strong>Total</strong></Table.Summary.Cell>
                            <Table.Summary.Cell index={11} align="right">
                                <strong style={{ color: totalSolde >= 0 ? '#389e0d' : '#cf1322' }}>
                                    {displayNumbers(totalSolde)} F
                                </strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={12} />
                        </Table.Summary.Row>
                    )}
                />
            )}
        </>
    );
};

// ─── Onglet 3 : Paiements ─────────────────────────────────────────────────────

const TabPaiements = ({ arrete, onRefresh }) => {
    const [chequeModal, setChequeModal] = useState(null); // retrocessionId en cours
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    const retrocessions = arrete.retrocessions || [];

    const ouvrirChequeModal = (r) => {
        form.setFieldsValue({
            numeroCheque:  r.numeroCheque  || '',
            banque:        r.banque        || '',
            montantCheque: r.montantCheque || r.soldeTechniqueNet || 0,
            dateCheque:    r.dateCheque ? dayjs(r.dateCheque) : dayjs(),
        });
        setChequeModal(r.retrocessionId);
    };

    const enregistrerCheque = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            await axiosInstance.put(`/api/vt/retrocessions/${chequeModal}/cheque`, {
                ...values,
                dateCheque: values.dateCheque.format('YYYY-MM-DD'),
            });
            toast.success('Chèque enregistré');
            setChequeModal(null);
            onRefresh();
        } catch {
            toast.error('Erreur lors de l\'enregistrement du chèque');
        } finally {
            setSaving(false);
        }
    };

    const marquerPaye = async (id) => {
        try {
            await axiosInstance.put(`/api/vt/retrocessions/${id}/payer`);
            toast.success('Rétrocession marquée comme payée');
            onRefresh();
        } catch {
            toast.error('Erreur lors du marquage comme payé');
        }
    };

    const annulerPaiement = async (id) => {
        try {
            await axiosInstance.put(`/api/vt/retrocessions/${id}/annuler-paiement`);
            toast.success('Paiement annulé');
            onRefresh();
        } catch {
            toast.error('Erreur lors de l\'annulation');
        }
    };

    if (arrete.statut === 'BROUILLON') {
        return <Alert type="info" message="Validez l'arrêté avant d'enregistrer les paiements." />;
    }

    return (
        <>
            {retrocessions.map((r) => (
                <Card
                    key={r.retrocessionId}
                    size="small"
                    style={{ marginBottom: 12 }}
                    title={
                        <span>
                            <BankOutlined style={{ marginRight: 8 }} />
                            {r.compagnieNom}
                        </span>
                    }
                    extra={
                        r.paid ? (
                            <>
                                <Tag color="green" icon={<CheckCircleOutlined />}>
                                    Payé le {r.datePaiement ? dayjs(r.datePaiement).format('DD/MM/YYYY') : '—'}
                                </Tag>
                                <Popconfirm
                                    title="Annuler ce paiement ?"
                                    onConfirm={() => annulerPaiement(r.retrocessionId)}
                                    okText="Oui" cancelText="Non"
                                >
                                    <Button size="small" danger style={{ marginLeft: 8 }}>Annuler</Button>
                                </Popconfirm>
                            </>
                        ) : (
                            <Button
                                type="primary"
                                size="small"
                                icon={<DollarOutlined />}
                                onClick={() => marquerPaye(r.retrocessionId)}
                            >
                                Marquer payé
                            </Button>
                        )
                    }
                >
                    <Row gutter={16} align="middle">
                        <Col xs={24} md={6}>
                            <Statistic
                                title="Solde technique net"
                                value={displayNumbers(r.soldeTechniqueNet)}
                                suffix="F"
                                valueStyle={{ color: (r.soldeTechniqueNet || 0) >= 0 ? '#389e0d' : '#cf1322', fontSize: 16 }}
                            />
                        </Col>
                        <Col xs={24} md={12}>
                            {r.numeroCheque ? (
                                <Descriptions size="small" column={2}>
                                    <Descriptions.Item label="N° chèque">{r.numeroCheque}</Descriptions.Item>
                                    <Descriptions.Item label="Banque">{r.banque || '—'}</Descriptions.Item>
                                    <Descriptions.Item label="Montant">{displayNumbers(r.montantCheque)} F</Descriptions.Item>
                                    <Descriptions.Item label="Date chèque">
                                        {r.dateCheque ? dayjs(r.dateCheque).format('DD/MM/YYYY') : '—'}
                                    </Descriptions.Item>
                                </Descriptions>
                            ) : (
                                <Text type="secondary">Aucun chèque enregistré</Text>
                            )}
                        </Col>
                        <Col xs={24} md={6} style={{ textAlign: 'right' }}>
                            <Button
                                icon={<FileTextOutlined />}
                                onClick={() => ouvrirChequeModal(r)}
                                disabled={r.paid}
                            >
                                {r.numeroCheque ? 'Modifier le chèque' : 'Saisir le chèque'}
                            </Button>
                        </Col>
                    </Row>
                </Card>
            ))}

            {/* Modal saisie chèque */}
            <Modal
                title="Enregistrer un chèque"
                open={!!chequeModal}
                onCancel={() => setChequeModal(null)}
                footer={[
                    <Button key="cancel" onClick={() => setChequeModal(null)}>Annuler</Button>,
                    <Button key="save" type="primary" loading={saving} onClick={enregistrerCheque}>
                        Enregistrer
                    </Button>,
                ]}
                width={440}
            >
                <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="numeroCheque"
                                label="N° chèque"
                                rules={[{ required: true, message: 'Obligatoire' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="banque" label="Banque">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="montantCheque"
                                label="Montant (FCFA)"
                                rules={[{ required: true, message: 'Obligatoire' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                    parser={(v) => v.replace(/\s/g, '')}
                                    min={0}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="dateCheque"
                                label="Date du chèque"
                                rules={[{ required: true, message: 'Obligatoire' }]}
                            >
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    );
};

// ─── Onglet 4 : Écriture comptable ───────────────────────────────────────────

const TabEcriture = ({ arrete }) => {
    const retrocessions = arrete.retrocessions || [];

    const lignesDebit = [
        { libelle: 'Taxes (6%)',                    montant: arrete.taxesTotal },
        { libelle: 'Commission STAC (20%)',          montant: arrete.commissionSTAC },
        { libelle: 'Transitaires (250 F/véh.)',      montant: arrete.commissionTransitaires },
        { libelle: 'Prévention routière (250 F/véh.)', montant: arrete.preventionRoutiere },
        { libelle: 'Sinistres payés après oct. 2012', montant: arrete.sinistresApresOct2012 },
        { libelle: 'Sinistres payés avant oct. 2012', montant: arrete.sinistresAvantOct2012 },
        { libelle: 'Charges exceptionnelles',        montant: arrete.chargesExceptionnelles },
        { libelle: 'Droit timbre',                   montant: arrete.droitTimbreTotal },
        ...retrocessions.map(r => ({
            libelle: `Solde technique — ${r.compagnieNom}`,
            montant: Math.max(0, r.soldeTechniqueNet || 0),
        })),
    ].filter(l => l.montant > 0);

    const lignesCredit = [
        { libelle: 'Primes nettes HT',          montant: arrete.primesNettesTotal },
        { libelle: 'Report mois précédent',      montant: arrete.reportMoisPrecedent },
        { libelle: 'Versements bancaires reçus', montant: arrete.totalVersementsBancaires },
    ].filter(l => l.montant > 0);

    const totalDebit  = lignesDebit.reduce((s, l)  => s + (l.montant || 0), 0);
    const totalCredit = lignesCredit.reduce((s, l) => s + (l.montant || 0), 0);
    const solde       = totalCredit - totalDebit;

    const columns = [
        { title: 'Libellé', dataIndex: 'libelle', key: 'lib' },
        {
            title: 'Débit (FCFA)',
            dataIndex: 'debit',
            align: 'right',
            render: (v) => v != null ? <span style={{ color: '#cf1322' }}>{displayNumbers(v)}</span> : '',
        },
        {
            title: 'Crédit (FCFA)',
            dataIndex: 'credit',
            align: 'right',
            render: (v) => v != null ? <span style={{ color: '#389e0d' }}>{displayNumbers(v)}</span> : '',
        },
    ];

    const rows = [
        ...lignesDebit.map(l  => ({ key: `d-${l.libelle}`, libelle: l.libelle, debit: l.montant, credit: null })),
        ...lignesCredit.map(l => ({ key: `c-${l.libelle}`, libelle: l.libelle, debit: null, credit: l.montant })),
    ];

    return (
        <Table
            dataSource={rows}
            columns={columns}
            rowKey="key"
            size="small"
            pagination={false}
            rowClassName={(r) => r.debit != null ? 'row-debit' : 'row-credit'}
            summary={() => (
                <>
                    <Table.Summary.Row style={{ background: '#fafafa' }}>
                        <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                            <strong style={{ color: '#cf1322' }}>{displayNumbers(totalDebit)}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                            <strong style={{ color: '#389e0d' }}>{displayNumbers(totalCredit)}</strong>
                        </Table.Summary.Cell>
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2}><strong>Solde</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                            <strong style={{ color: solde >= 0 ? '#389e0d' : '#cf1322', fontSize: 15 }}>
                                {displayNumbers(Math.abs(solde))} F {solde >= 0 ? '(excédent)' : '(déficit)'}
                            </strong>
                        </Table.Summary.Cell>
                    </Table.Summary.Row>
                </>
            )}
        />
    );
};

// ─── Composant principal ──────────────────────────────────────────────────────

const ArreteVTDetail = () => {
    const { arreteId }             = useParams();
    const navigate                 = useNavigate();
    const [arrete, setArrete]      = useState(null);
    const [loading, setLoading]    = useState(true);
    const [validating, setValidating] = useState(false);
    const [activeTab, setActiveTab] = useState('recap');

    const chargerArrete = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/vt/arretes/${arreteId}`);
            setArrete(res.data);
        } catch {
            toast.error('Erreur lors du chargement de l\'arrêté');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { chargerArrete(); }, [arreteId]);

    const validerArrete = async () => {
        setValidating(true);
        try {
            await axiosInstance.post(`/api/vt/arretes/${arreteId}/valider`);
            toast.success('Arrêté validé — rétrocessions créées');
            chargerArrete();
            setActiveTab('retro');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Erreur lors de la validation');
        } finally {
            setValidating(false);
        }
    };

    const moisLabel = arrete?.moisArrete
        ? dayjs(arrete.moisArrete).format('MMMM YYYY').replace(/^\w/, c => c.toUpperCase())
        : '…';

    const statutCfg = arrete ? (STATUT_CONFIG[arrete.statut] || {}) : {};

    const tabs = [
        {
            key: 'recap',
            label: <span><FileTextOutlined /> Récapitulatif</span>,
            children: arrete ? <TabRecap arrete={arrete} /> : null,
        },
        {
            key: 'retro',
            label: <span><SafetyCertificateOutlined /> RETRO</span>,
            children: arrete ? (
                <TabRetro arrete={arrete} onValider={validerArrete} validating={validating} />
            ) : null,
        },
        {
            key: 'paiements',
            label: <span><BankOutlined /> Paiements</span>,
            children: arrete ? <TabPaiements arrete={arrete} onRefresh={chargerArrete} /> : null,
        },
        {
            key: 'ecriture',
            label: <span><BookOutlined /> Écriture comptable</span>,
            children: arrete ? <TabEcriture arrete={arrete} /> : null,
        },
    ];

    return (
        <ConfigProvider locale={frFR}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                <SideBar currentMenu="op-retro-vt" />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
                            ) : !arrete ? (
                                <Alert type="error" message="Arrêté introuvable." />
                            ) : (
                                <>
                                    {/* En-tête */}
                                    <div className="d-sm-flex align-items-center justify-content-between mb-4">
                                        <div>
                                            <Button
                                                type="link"
                                                icon={<ArrowLeftOutlined />}
                                                onClick={() => navigate('/retrocession-vt/arretes')}
                                                style={{ padding: 0, marginBottom: 4 }}
                                            >
                                                Retour à la liste
                                            </Button>
                                            <Title level={4} style={{ margin: 0 }}>
                                                Arrêté VT — {moisLabel}
                                            </Title>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <Tag color={statutCfg.color} icon={statutCfg.icon} style={{ fontSize: 14, padding: '4px 12px' }}>
                                                {statutCfg.label}
                                            </Tag>
                                            <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
                                                Imprimer
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Synthèse rapide */}
                                    <Row gutter={16} style={{ marginBottom: 16 }}>
                                        <Col xs={12} md={4}>
                                            <Card size="small">
                                                <Statistic title="VL" value={arrete.nbVL || 0} />
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Card size="small">
                                                <Statistic title="Camions" value={arrete.nbCamions || 0} />
                                            </Card>
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Card size="small">
                                                <Statistic
                                                    title="Primes nettes HT"
                                                    value={displayNumbers(arrete.primesNettesTotal)}
                                                    suffix="F"
                                                    valueStyle={{ color: '#1677ff' }}
                                                />
                                            </Card>
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Card size="small">
                                                <Statistic
                                                    title="Commission STAC"
                                                    value={displayNumbers(arrete.commissionSTAC)}
                                                    suffix="F"
                                                />
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Onglets */}
                                    <Card>
                                        <Tabs
                                            activeKey={activeTab}
                                            onChange={setActiveTab}
                                            items={tabs}
                                        />
                                    </Card>
                                </>
                            )}
                        </div>
                    </div>
                    <Footer />
                </div>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default ArreteVTDetail;
